import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React from "react";
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import Button from "@mui/material/Button";
import Alert from "@mui/material/Alert";
import Snackbar from "@mui/material/Snackbar";
import Typography from "@mui/material/Typography";
import { alpha } from "@mui/material/styles";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import LogoutRoundedIcon from "@mui/icons-material/LogoutRounded";
import { useTranslation } from "../../hooks";
import { usePortalStore } from "../../store/portal/PortalStoreContext";
import { usePortalDashboardData, useAppointmentTabs, } from "../../queries/portal/usePortalDashboardData";
import { usePortalSignOut } from "../../queries/portal/usePortalQueries";
import { useCancelAppointmentMutation, useSelfAttestMutation, } from "../../queries/portal/usePortalMutations";
import { extractApiError } from "../../utils/extractApiError";
import { formatMoney } from "./appointmentPresentation";
import { AppointmentList } from "./AppointmentList";
import { PatientPicker } from "./PatientPicker";
import { PatientRecordsSection } from "./PatientRecordsSection";
import { SessionPacksSection } from "./SessionPacksSection";
import { WalletCard } from "./WalletCard";
import { CancelAppointmentDialog } from "./CancelAppointmentDialog";
import { PortalPaymentClaimSheet } from "./PortalPaymentClaimSheet";
import { LanguageSelector } from "../shared/LanguageSelector";
import { PortalSection } from "./ui/PortalSection";
function DashboardHeader({ greetingName, phone, onBook, onSignOut, }) {
    const { t, language, changeLanguage } = useTranslation("portal");
    return (_jsxs(Box, { sx: (theme) => ({
            position: "relative",
            overflow: "hidden",
            borderRadius: 2,
            mb: 3,
            p: { xs: 2, sm: 2.5 },
            border: `1px solid ${alpha(theme.palette.primary.main, 0.16)}`,
            background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.1)} 0%, ${alpha(theme.palette.primary.main, 0.03)} 55%, ${alpha(theme.palette.primary.main, 0.08)} 100%)`,
        }), children: [_jsxs(Stack, { direction: "row", spacing: 1, sx: { alignItems: "flex-start", justifyContent: "space-between" }, children: [_jsxs(Box, { sx: { minWidth: 0 }, children: [_jsx(Typography, { component: "h2", sx: {
                                    fontWeight: 700,
                                    fontSize: { xs: 20, sm: 24 },
                                    lineHeight: 1.2,
                                    wordBreak: "break-word",
                                }, children: greetingName
                                    ? t("greeting", "Hi, {{name}}", { name: greetingName })
                                    : t("dashboardTitle", "Your appointments") }), phone ? (_jsx(Typography, { variant: "caption", sx: { color: "text.secondary", display: "block", mt: 0.5 }, children: t("signedInAs", "Signed in as {{phone}}", { phone }) })) : null] }), _jsxs(Stack, { direction: "row", spacing: 1, sx: { flexShrink: 0 }, children: [_jsx(LanguageSelector, { language: language, onChange: changeLanguage, label: t("language", "Language"), compact: true }), _jsx(Button, { size: "small", onClick: onSignOut, startIcon: _jsx(LogoutRoundedIcon, { sx: { fontSize: 16 } }), sx: {
                                    flexShrink: 0,
                                    color: "text.secondary",
                                    px: 1.25,
                                    py: 0.5,
                                    borderRadius: 999,
                                }, children: t("signOut", "Sign out") })] })] }), _jsx(Button, { fullWidth: true, variant: "contained", size: "large", onClick: onBook, startIcon: _jsx(AddRoundedIcon, {}), sx: { mt: 2.5, py: 1.25, borderRadius: 2, fontSize: 15 }, children: t("bookAppointment", "Book new appointment") })] }));
}
function describeCancelOutcome(result, t) {
    const res = result;
    const credits = res?.creditReturned;
    if (typeof credits === "number" && credits > 0) {
        return t("cancelSuccessCredits", "Appointment cancelled. {{count}} session credit returned to your wallet.", { count: credits });
    }
    const amount = res?.refundAmount;
    if (typeof amount === "number" && amount > 0) {
        const money = formatMoney(amount, res?.currency);
        return res?.refundChannel === "MANUAL"
            ? t("cancelSuccessManual", "Appointment cancelled. Clinic staff will return {{amount}} to you in person.", { amount: money })
            : t("cancelSuccessRefund", "Appointment cancelled. {{amount}} refunded.", {
                amount: money,
            });
    }
    return t("cancelSuccess", "Appointment cancelled.");
}
export function PortalDashboard({ onReschedule, }) {
    const { t } = useTranslation("portal");
    const tab = usePortalStore((s) => s.tab);
    const setTab = usePortalStore((s) => s.setTab);
    const scope = usePortalStore((s) => s.patientScope);
    const setScope = usePortalStore((s) => s.setPatientScope);
    const startBooking = usePortalStore((s) => s.startBooking);
    const signOut = usePortalSignOut();
    const countryCode = usePortalStore((s) => s.countryCode);
    const phoneNumber = usePortalStore((s) => s.phoneNumber);
    const { patients, patientsQuery, walletPacks, packs, packsLoading, appointments, appointmentsQuery, methods, addressId, currency, } = usePortalDashboardData();
    const byTab = useAppointmentTabs(appointments);
    const cancelMutation = useCancelAppointmentMutation();
    const attestMutation = useSelfAttestMutation();
    const [cancelTarget, setCancelTarget] = React.useState(null);
    const [claimTarget, setClaimTarget] = React.useState(null);
    const [attestingId, setAttestingId] = React.useState(null);
    const [attestingStatus, setAttestingStatus] = React.useState(null);
    const [attestError, setAttestError] = React.useState(null);
    const [cancelOutcome, setCancelOutcome] = React.useState(null);
    const openCancel = (appointment) => {
        cancelMutation.beginAttempt();
        cancelMutation.reset();
        setCancelTarget(appointment);
    };
    const handleSelfAttest = (appointment, status) => {
        if (attestingId !== null)
            return;
        attestMutation.beginAttempt();
        setAttestingId(appointment.id);
        setAttestingStatus(status);
        setAttestError(null);
        attestMutation.mutate({ appointmentId: appointment.id, status }, {
            onError: (error) => setAttestError({
                appointmentId: appointment.id,
                message: extractApiError(error, t("attestFailed", "We couldn't record that. Please try again, or let the clinic know.")),
            }),
            onSettled: () => {
                setAttestingId(null);
                setAttestingStatus(null);
            },
        });
    };
    const greetingName = patients[0]?.firstName ?? null;
    const phone = phoneNumber ? `${countryCode} ${phoneNumber}` : null;
    return (_jsxs(Box, { children: [_jsx(DashboardHeader, { greetingName: greetingName, phone: phone, onBook: startBooking, onSignOut: signOut }), _jsx(WalletCard, { packs: walletPacks }), patientsQuery.isError ? (_jsx(Alert, { severity: "error", sx: { mb: 2, borderRadius: 2 }, children: extractApiError(patientsQuery.error, t("loadFailed", "We couldn't load your appointments.")) })) : null, _jsxs(PortalSection, { title: t("dashboardTitle", "Your appointments"), children: [_jsx(PatientPicker, { patients: patients, scope: scope ?? "ALL", onChange: setScope }), _jsx(AppointmentList, { tab: tab, onTabChange: setTab, appointments: byTab[tab], loading: appointmentsQuery.isPending, error: appointmentsQuery.isError ? appointmentsQuery.error : null, onRetry: () => appointmentsQuery.refetch(), showPatientName: scope === "ALL", hasManualPaymentMethods: methods.length > 0, busyAppointmentId: attestingId, busyAttestStatus: attestingStatus, attestError: attestError?.message ?? null, attestErrorAppointmentId: attestError?.appointmentId ?? null, onCancel: openCancel, onReschedule: onReschedule, onSelfAttest: handleSelfAttest, onClaimPayment: setClaimTarget })] }), _jsx(PatientRecordsSection, { patients: patients, addressId: addressId, onAdded: () => patientsQuery.refetch() }), _jsx(SessionPacksSection, { packs: packs, loading: packsLoading, showPatientName: patients.length > 1, currency: currency }), _jsx(CancelAppointmentDialog, { open: cancelTarget !== null, appointment: cancelTarget, submitting: cancelMutation.isPending, error: cancelMutation.isError
                    ? extractApiError(cancelMutation.error, "We could not cancel that appointment.")
                    : null, onClose: () => setCancelTarget(null), onConfirm: (reason) => {
                    if (!cancelTarget)
                        return;
                    cancelMutation.mutate({
                        appointmentId: cancelTarget.id,
                        cancellationReason: reason || undefined,
                    }, {
                        onSuccess: (result) => {
                            setCancelTarget(null);
                            setCancelOutcome(describeCancelOutcome(result, t));
                        },
                    });
                } }), _jsx(Snackbar, { open: cancelOutcome !== null, autoHideDuration: 6000, onClose: () => setCancelOutcome(null), anchorOrigin: { vertical: "bottom", horizontal: "center" }, children: _jsx(Alert, { severity: "success", onClose: () => setCancelOutcome(null), sx: { borderRadius: 2 }, children: cancelOutcome }) }), _jsx(PortalPaymentClaimSheet, { open: claimTarget !== null, appointment: claimTarget, methods: methods, onClose: () => setClaimTarget(null), onClaimed: () => {
                    setClaimTarget(null);
                    appointmentsQuery.refetch();
                } })] }));
}
